import { useCallback, useEffect, useRef, useState } from "react";

export type PhoneticCandidate = {
  text: string;
  confidence: number;
};

type ActiveRange = {
  start: number;
  end: number;
};

type ResolveRequest = {
  text: string;
  caretPosition: number;
  contextText?: string;
  isComposing?: boolean;
  maxCandidates?: number;
};

type ResolveResponse = {
  success?: boolean;
  result?: {
    engine: "PhoneticResolver";
    reading: string;
    candidates: PhoneticCandidate[];
  };
  error?: {
    code?: string;
    message?: string;
  };
};

const MAX_TOKEN_LENGTH = 24;
const TOKEN_REGEX = /[A-Za-zぁ-んァ-ンー一-龯]{2,24}$/u;

export function extractResolverTokenBeforeCaret(text: string, caretPosition: number) {
  const clampedCaret = Math.max(0, Math.min(caretPosition, text.length));
  const beforeCaret = text.slice(0, clampedCaret);
  const match = beforeCaret.match(TOKEN_REGEX);

  if (!match) {
    return null;
  }

  const token = match[0];
  if (token.length > MAX_TOKEN_LENGTH) {
    return null;
  }

  const start = clampedCaret - token.length;
  return {
    token,
    start,
    end: clampedCaret,
  };
}

export function usePhoneticResolver() {
  const [candidates, setCandidates] = useState<PhoneticCandidate[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [activeRange, setActiveRange] = useState<ActiveRange | null>(null);
  const [activeReading, setActiveReading] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const clearCandidates = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setCandidates([]);
    setSelectedIndex(0);
    setActiveRange(null);
    setActiveReading(null);
    setIsLoading(false);
  }, []);

  const resolveCandidates = useCallback(
    async ({
      text,
      caretPosition,
      contextText,
      isComposing,
      maxCandidates = 3,
    }: ResolveRequest) => {
      if (isComposing) {
        clearCandidates();
        return;
      }

      const tokenMatch = extractResolverTokenBeforeCaret(text, caretPosition);
      if (!tokenMatch) {
        clearCandidates();
        return;
      }

      const currentRequestId = ++requestIdRef.current;
      const normalizedReading = tokenMatch.token.trim();
      if (!normalizedReading) {
        clearCandidates();
        return;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }

      debounceRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortRef.current = controller;
        setIsLoading(true);

        try {
          const response = await fetch("/api/phonetic-resolve", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              reading: normalizedReading,
              contextText: contextText ?? text,
              maxCandidates,
            }),
            signal: controller.signal,
          });

          const payload = (await response.json()) as ResolveResponse;
          if (!response.ok || !payload?.success || !payload.result) {
            clearCandidates();
            return;
          }

          if (currentRequestId !== requestIdRef.current) {
            return;
          }

          const nextCandidates = payload.result.candidates.filter((candidate) => candidate.text.trim());
          if (nextCandidates.length === 0) {
            clearCandidates();
            return;
          }

          setCandidates(nextCandidates);
          setSelectedIndex(0);
          setActiveRange({ start: tokenMatch.start, end: tokenMatch.end });
          setActiveReading(payload.result.reading);
        } catch (error) {
          if ((error as Error).name !== "AbortError") {
            clearCandidates();
          }
        } finally {
          if (currentRequestId === requestIdRef.current) {
            setIsLoading(false);
          }
        }
      }, 250);
    },
    [clearCandidates],
  );

  const selectNextCandidate = useCallback(() => {
    setSelectedIndex((prev) => {
      if (candidates.length === 0) return 0;
      return (prev + 1) % candidates.length;
    });
  }, [candidates.length]);

  const selectPreviousCandidate = useCallback(() => {
    setSelectedIndex((prev) => {
      if (candidates.length === 0) return 0;
      return (prev - 1 + candidates.length) % candidates.length;
    });
  }, [candidates.length]);

  const commitSelection = useCallback(
    (text: string, fallbackCaretPosition: number, index = selectedIndex) => {
      if (!activeRange || candidates.length === 0) {
        return {
          replaced: false,
          text,
          caretPosition: fallbackCaretPosition,
        };
      }

      const candidate = candidates[index];
      if (!candidate) {
        return {
          replaced: false,
          text,
          caretPosition: fallbackCaretPosition,
        };
      }

      const nextText = `${text.slice(0, activeRange.start)}${candidate.text}${text.slice(activeRange.end)}`;
      const nextCaret = activeRange.start + candidate.text.length;

      setCandidates([]);
      setSelectedIndex(0);
      setActiveRange(null);
      setActiveReading(null);

      return {
        replaced: true,
        text: nextText,
        caretPosition: nextCaret,
      };
    },
    [activeRange, candidates, selectedIndex],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  return {
    activeReading,
    activeRange,
    candidates,
    clearCandidates,
    commitSelection,
    isLoading,
    resolveCandidates,
    selectedIndex,
    selectNextCandidate,
    selectPreviousCandidate,
    setSelectedIndex,
  };
}
